import ChatIcon from "../assets/li_chat.svg";
import MailIcon from "../assets/li_mail.svg";
import PhoneIcon from "../assets/li_phone.svg";
import { useMainContext } from "../context/MainContext.tsx";
import { useMergedConfig } from "../context/MergedConfigProvider.tsx";
import { useTranslations } from '../hooks/useTranslations.ts';
import { translationKeys } from '../translations/translationKeys.ts';
import { nonSpectreJourney } from '../utils/backgroundImage.ts';
import { useIsMobile } from '../utils/useIsMobile.ts';
import LinkButton from "./LinkButton";

type ContactAction = {
  label: string;
  icon: string;
  alt: string;
  href?: string;
  onClick?: () => void;
};

const getPhoneContact = (journeyId?: string, region?: string | null): ContactAction => {
  const formattedRegion = region ? region.toLowerCase() : "";

  if (formattedRegion === "ie" || formattedRegion === "ireland") {
    return {
      label: "+353 (0)1 256 3060",
      icon: PhoneIcon,
      alt: "Call support",
      href: "tel:+353012563060",
    };
  }

  if (journeyId === "spectre" || journeyId === "spectreretail") {
    return {
      label: "+44 (0)203 003 8844",
      icon: PhoneIcon,
      alt: "Call support",
      href: "tel:+4402030038844",
    };
  }

  return {
    label: "+44 (0) 20 7170 8209",
    icon: PhoneIcon,
    alt: "Call support",
    href: "tel:+442071708209",
  };
};

const baseContactItems: ContactAction[] = [
  {
    label: "clientmanagement@cmcmarkets.co.uk",
    icon: MailIcon,
    alt: "Email support",
    href: "mailto:clientmanagement@cmcmarkets.co.uk",
  },
];

interface ContactProps {
  isChatLoading: boolean;
  openLiveChatBtnOnClick: () => void;
}

export default function Contact({
  isChatLoading,
  openLiveChatBtnOnClick,
}: ContactProps) {
  const { journeyId } = useMainContext();
  const { embedded, region } = useMergedConfig();
  const isMobile = useIsMobile();
  const { translate } = useTranslations();

  const phoneContact = getPhoneContact(journeyId, region);

  const items: ContactAction[] = [
    phoneContact,
    ...baseContactItems,
    ...(embedded || isMobile
      ? []
      : [
          {
            label: "Start a conversation now",
            icon: ChatIcon,
            alt: "Open live chat",
            onClick: openLiveChatBtnOnClick,
          },
        ]),
  ];

  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-base font-normal leading-6 text-content-primary">
        {translate(translationKeys.CONTACT_WERE_HERE_FOR_YOU)}
      </h3>
      <div className="flex flex-col gap-4">
        {items.map(({ label, icon, alt, href, onClick }) => (
          <div key={label}>
            <LinkButton
              href={href}
              onClick={onClick}
              isLoading={!!onClick && isChatLoading}
            >
              <img
                src={icon}
                alt={alt}
                className="h-4 w-4"
                style={
                  nonSpectreJourney(journeyId)
                    ? { filter: "brightness(0)" }
                    : undefined
                }
              />
              <span>{label}</span>
            </LinkButton>
          </div>
        ))}
      </div>
    </section>
  );
}
